/**
 * Step 2 -- Billable days (f10.5), rendered inside f10.3's step frame through
 * `STEP_BODIES`. Follows `cycle-step.html`:
 *
 *   upload -> process -> what did not match -> decide -> figures -> accept
 *
 * Shows the newest batch that has not been discarded. Upload and commit each
 * answer `202` with a job, watched through the step's own job route; on a
 * terminal state the batch (and, after a commit, the rail) is refetched.
 */

import { useEffect, useRef, useState } from 'react';
import { Check, X } from 'lucide-react';

import type { BillableDaysReview, CycleWorkflowStep } from '@/api/client';
import {
  isJobTerminal,
  useBillableDaysBatches,
  useBillableDaysJob,
  useBillableDaysReview,
  useCommitBillableDaysBatch,
  useDiscardBillableDaysBatch,
  useRefreshBillableDays,
  useUploadBillableDaysBatch,
} from '@/api/queries';
import { JobProgress } from '@/jobs/JobProgress';

import { BillableDaysCommitDialog } from './BillableDaysCommitDialog';
import { BillableDaysDropzone, BillableDaysFileCard, BillableDaysProcessingCard } from './BillableDaysFileCards';
import { BillableDaysFiguresCard } from './BillableDaysFiguresCard';
import { UnmatchedCountriesCard } from './UnmatchedCountriesCard';
import { hasStagedItems, isParsing, refusalMessage } from './billableDaysView';

interface WatchedJob {
  id: string;
  kind: 'parse' | 'commit';
}

export function BillableDaysStep({ cycleId }: { cycleId: string; step: CycleWorkflowStep }) {
  const batches = useBillableDaysBatches(cycleId);
  const upload = useUploadBillableDaysBatch(cycleId);
  const [watched, setWatched] = useState<WatchedJob | null>(null);

  const shown = batches.data?.batches.find((batch) => batch.state.code !== 'DISCARDED');
  const parsing = shown ? isParsing(shown.state.code) : false;
  const review = useBillableDaysReview(cycleId, shown && !parsing ? shown.id : undefined);
  const job = useBillableDaysJob(cycleId, watched?.id);

  useJobCompletion(cycleId, watched, job.data?.state);

  const startUpload = (file: File) =>
    upload.mutate(file, { onSuccess: (accepted) => setWatched({ id: accepted.job_id, kind: 'parse' }) });

  if (batches.isPending) return <p className="hint">Loading step 2…</p>;
  if (batches.isError) {
    return <ErrorAlert message={refusalMessage(batches.error) ?? 'Step 2 could not be loaded.'} />;
  }

  const jobCard = job.data && (!isJobTerminal(job.data.state) || job.data.state === 'FAILED') && (
    <div className="card">
      <div className="card-body">
        <JobProgress
          job={job.data}
          title={watched?.kind === 'commit' ? 'Storing billable days' : 'Reading the billable-days report'}
        />
      </div>
    </div>
  );

  return (
    <div className="stack">
      {upload.error ? <ErrorAlert message={refusalMessage(upload.error)} /> : null}
      {jobCard}

      {!shown && <BillableDaysDropzone onFile={startUpload} busy={upload.isPending} />}

      {shown && parsing && !jobCard && (
        <p className="hint" role="status">
          Reading {shown.original_filename ?? 'the uploaded file'}…
        </p>
      )}

      {review.isError && <ErrorAlert message={refusalMessage(review.error)} />}

      {review.data && (
        <>
          <BillableDaysFileCard review={review.data} onReplace={startUpload} busy={upload.isPending} />
          <BillableDaysProcessingCard review={review.data} />
          {hasStagedItems(review.data.batch.state.code) && (
            <BatchDecisions
              cycleId={cycleId}
              review={review.data}
              onCommitStarted={(jobId) => setWatched({ id: jobId, kind: 'commit' })}
            />
          )}
        </>
      )}
    </div>
  );
}

/** Refetch once a watched job reaches a terminal state -- once per job. */
function useJobCompletion(cycleId: string, watched: WatchedJob | null, state: string | undefined) {
  const refresh = useRefreshBillableDays(cycleId);
  const handled = useRef<string | null>(null);

  useEffect(() => {
    if (!watched || !isJobTerminal(state) || handled.current === watched.id) return;
    handled.current = watched.id;
    refresh(watched.kind === 'commit' && state === 'SUCCEEDED');
  }, [watched, state, refresh]);
}

function BatchDecisions({
  cycleId,
  review,
  onCommitStarted,
}: {
  cycleId: string;
  review: BillableDaysReview;
  onCommitStarted: (jobId: string) => void;
}) {
  const commit = useCommitBillableDaysBatch(cycleId, review.batch.id);
  const discard = useDiscardBillableDaysBatch(cycleId, review.batch.id);
  const [confirming, setConfirming] = useState(false);
  const { capabilities } = review;

  const footer = (capabilities.can_commit || capabilities.can_discard) && (
    <div className="card-foot">
      <span className="hint">
        {capabilities.can_commit
          ? 'Every country is decided. Accepting stores the billable days and completes step 2.'
          : 'Decide every unmatched country above to enable Accept and commit.'}
      </span>
      <div className="btn-row">
        {capabilities.can_discard && (
          <button type="button" className="btn btn-secondary" disabled={discard.isPending}
            onClick={() => discard.mutate()}>
            <X aria-hidden="true" /> Discard batch
          </button>
        )}
        <button type="button" className="btn btn-primary" disabled={!capabilities.can_commit}
          onClick={() => setConfirming(true)}>
          <Check aria-hidden="true" /> Accept and commit
        </button>
      </div>
    </div>
  );

  return (
    <>
      <UnmatchedCountriesCard cycleId={cycleId} review={review} />
      {discard.error ? <ErrorAlert message={refusalMessage(discard.error)} /> : null}
      <BillableDaysFiguresCard figures={review.figures} footer={footer || undefined} />
      {confirming && (
        <BillableDaysCommitDialog
          review={review}
          busy={commit.isPending}
          error={commit.error}
          onClose={() => {
            setConfirming(false);
            commit.reset();
          }}
          onConfirm={() =>
            commit.mutate(undefined, {
              onSuccess: (accepted) => {
                setConfirming(false);
                onCommitStarted(accepted.job_id);
              },
            })
          }
        />
      )}
    </>
  );
}

function ErrorAlert({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="alert alert-danger" role="alert">
      <div>
        <div className="a-body">{message}</div>
      </div>
    </div>
  );
}
